import React, { useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE } from '../config/api'; // <- ajustá la ruta si tu api.js está en otro lugar

const FormularioProfesor = () => {
  const [tipoPedido, setTipoPedido] = useState('');
  const [colegios, setColegios] = useState([]);
  const [salas, setSalas] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [componentes, setComponentes] = useState([]);

  const [colegioSeleccionado, setColegioSeleccionado] = useState('');
  const [salaSeleccionada, setSalaSeleccionada] = useState('');
  const [equipoSeleccionado, setEquipoSeleccionado] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [componenteSeleccionado, setComponenteSeleccionado] = useState('');
  const [cantidad, setCantidad] = useState(1);
  const [busquedaComponente, setBusquedaComponente] = useState('');
  const [componenteBuscadorAbierto, setComponenteBuscadorAbierto] = useState(false);
  const [busquedaColegio, setBusquedaColegio] = useState('');
  const [colegioBuscadorAbierto, setColegioBuscadorAbierto] = useState(false);
  const [enviandoPedido, setEnviandoPedido] = useState(false);
  const enviandoPedidoRef = useRef(false);

  const profesorId = localStorage.getItem('profesorId');
  const area = localStorage.getItem('profesorArea');

  const normalizarTexto = (valor) =>
    String(valor || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const areaNormalizada = normalizarTexto(area);
  const puedePedirKitRobotica = areaNormalizada === 'robotica' || areaNormalizada === 'ambas';

  const colegiosFiltrados = useMemo(() => {
    const texto = normalizarTexto(busquedaColegio.trim());
    if (!texto) return colegios.slice(0, 8);

    return colegios
      .filter((colegio) => normalizarTexto(colegio.nombre).includes(texto))
      .slice(0, 8);
  }, [colegios, busquedaColegio]);

  const componentesKitRobotica = useMemo(() => {
    const texto = normalizarTexto(busquedaComponente.trim());
    return (componentes || [])
      .filter((componente) => {
        const areaComponente = normalizarTexto(componente.area_destinada);
        return areaComponente === 'robotica' || areaComponente === 'ambas';
      })
      .filter((componente) => {
        if (!texto) return true;
        return (
          normalizarTexto(componente.nombre).includes(texto) ||
          normalizarTexto(componente.codigo_unico).includes(texto)
        );
      })
      .slice(0, 10);
  }, [componentes, busquedaComponente]);

  const seleccionarColegio = (colegio) => {
    setColegioSeleccionado(String(colegio.id));
    setBusquedaColegio(colegio.nombre);
    setColegioBuscadorAbierto(false);
    setSalaSeleccionada('');
    setEquipoSeleccionado('');
  };

  const limpiarColegio = () => {
    setColegioSeleccionado('');
    setBusquedaColegio('');
    setColegioBuscadorAbierto(false);
    setSalaSeleccionada('');
    setEquipoSeleccionado('');
  };

  const seleccionarComponente = (componente) => {
    setComponenteSeleccionado(String(componente.id));
    setBusquedaComponente(`${componente.nombre}${componente.codigo_unico ? ` (${componente.codigo_unico})` : ''}`);
    setComponenteBuscadorAbierto(false);
  };

  const limpiarComponente = () => {
    setComponenteSeleccionado('');
    setBusquedaComponente('');
    setComponenteBuscadorAbierto(false);
    setCantidad(1);
  };

  // Helper: fetch seguro (evita Script error por respuestas no-JSON o fetch fallido)
  const fetchJSON = async (url) => {
    const res = await fetch(url);
    const text = await res.text();
    try {
      const json = text ? JSON.parse(text) : null;
      if (!res.ok) throw new Error(json?.mensaje || `HTTP ${res.status}`);
      return json;
    } catch (e) {
      // si no es JSON o hay error, tiramos error para el catch
      if (!res.ok) throw e;
      return null;
    }
  };

  // Cargar colegios asociados + componentes visibles
  useEffect(() => {
    if (!profesorId) return;

    fetchJSON(`${API_BASE}/api/profesores/${profesorId}/colegios`)
      .then((data) => setColegios(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error('Error cargando colegios del profesor:', err);
        setColegios([]);
      });

    fetchJSON(`${API_BASE}/api/componentes/${profesorId}`)
      .then((data) => setComponentes(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error('Error cargando componentes:', err);
        setComponentes([]);
      });
  }, [profesorId]); // OK

  // Cargar salas por colegio
  useEffect(() => {
    if (!colegioSeleccionado) {
      setSalas([]);
      setSalaSeleccionada('');
      return;
    }

    fetchJSON(`${API_BASE}/api/colegios/${colegioSeleccionado}/salas`)
      .then((data) => setSalas(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error('Error cargando salas:', err);
        setSalas([]);
        setSalaSeleccionada('');
      });
  }, [colegioSeleccionado]);

  // Cargar equipos por sala
  useEffect(() => {
    if (!salaSeleccionada) {
      setEquipos([]);
      setEquipoSeleccionado('');
      return;
    }

    fetchJSON(`${API_BASE}/api/salas/${salaSeleccionada}/equipos`)
      .then((data) => setEquipos(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error('Error cargando equipos:', err);
        setEquipos([]);
        setEquipoSeleccionado('');
      });
  }, [salaSeleccionada]);

  // Si cambio de tipo y ya no corresponde equipo/componente, limpio campos
  useEffect(() => {
    if (tipoPedido !== 'soporte') setEquipoSeleccionado('');
    if (tipoPedido !== 'kit_robotica') {
      setComponenteSeleccionado('');
      setBusquedaComponente('');
      setCantidad(1);
    }
  }, [tipoPedido]);

  const enviarPedido = async (e) => {
    e.preventDefault();
    if (enviandoPedidoRef.current) return;

    let tipoFinal = '';
    if (tipoPedido === 'soporte') tipoFinal = 'Soporte Técnico';
    else if (tipoPedido === 'soporte_sala') tipoFinal = 'Soporte de Sala';
    else if (tipoPedido === 'insumo') tipoFinal = 'Insumos';
    else if (tipoPedido === 'mobiliario') tipoFinal = 'Mobiliario';
    else if (tipoPedido === 'kit_robotica') tipoFinal = 'Kit de Robótica';

    let descripcionFinal = descripcion;

    if (!colegioSeleccionado) {
      alert('Debes seleccionar un colegio.');
      return;
    }

    if (!salaSeleccionada) {
      alert('Debes seleccionar una sala.');
      return;
    }

    // Validación: en soporte técnico sí debe haber equipo
    if (tipoPedido === 'soporte' && !equipoSeleccionado) {
      alert('Debes seleccionar un equipo para Soporte Técnico.');
      return;
    }

    if (tipoPedido === 'kit_robotica') {
      const componenteElegido = componentes.find(
        (componente) => String(componente.id) === String(componenteSeleccionado)
      );
      const observacionKit = descripcion.trim();

      if (!componenteElegido) {
        const confirmarSinItem = window.confirm(
          'No seleccionaste ningún componente de la lista.\n\n' +
            'Si el artículo no existe en la lista, podés enviarlo igualmente, pero mencionalo en el detalle.\n\n' +
            '¿Querés enviar el pedido sin seleccionar un componente?'
        );

        if (!confirmarSinItem) return;

        if (!observacionKit) {
          alert('Agregá en la descripción qué artículo o kit necesitás.');
          return;
        }

        descripcionFinal = `Kit de Robótica solicitado: no seleccionado en lista.\nDetalle docente: ${observacionKit}`;
      } else {
        const cantidadKit = Math.max(1, parseInt(cantidad, 10) || 1);
        descripcionFinal = [
          `Kit de Robótica solicitado: ${componenteElegido.nombre}${componenteElegido.codigo_unico ? ` (${componenteElegido.codigo_unico})` : ''}`,
          `Cantidad solicitada: ${cantidadKit}`,
          observacionKit ? `Observación docente: ${observacionKit}` : null,
        ]
          .filter(Boolean)
          .join('\n');
      }
    }

    const cuerpo = {
      tipo_pedido: tipoFinal,
      descripcion_problema: descripcionFinal,
      profesor_id: profesorId,
      colegio_id: colegioSeleccionado,
      sala_id: salaSeleccionada,
      // Soporte técnico: lleva equipo. Soporte de sala: NO.
      equipo_id: tipoPedido === 'soporte' ? equipoSeleccionado : null,
      idempotency_key:
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${profesorId}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    };

    try {
      enviandoPedidoRef.current = true;
      setEnviandoPedido(true);

      const res = await fetch(`${API_BASE}/api/pedidos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Idempotency-Key': cuerpo.idempotency_key,
        },
        body: JSON.stringify(cuerpo),
      });

      const text = await res.text();
      let json = null;
      try { json = text ? JSON.parse(text) : null; } catch {}

      if (!res.ok) {
        alert(json?.mensaje || 'Error al enviar el pedido');
        return;
      }

      alert(json?.duplicado ? 'El pedido ya estaba registrado' : 'Pedido enviado correctamente');
      setTipoPedido('');
      limpiarColegio();
      setSalaSeleccionada('');
      setEquipoSeleccionado('');
      setDescripcion('');
      setComponenteSeleccionado('');
      setBusquedaComponente('');
      setCantidad(1);
    } catch (err) {
      console.error('Error enviando pedido:', err);
      alert('Error de conexión con el servidor');
    } finally {
      enviandoPedidoRef.current = false;
      setEnviandoPedido(false);
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-4 text-blue-700">Nuevo Pedido</h2>

      <form onSubmit={enviarPedido} className="bg-white p-6 rounded shadow space-y-4 max-w-xl">
        <select
          value={tipoPedido}
          onChange={(e) => setTipoPedido(e.target.value)}
          className="w-full border px-3 py-2 rounded"
          required
        >
          <option value="">-- Tipo de pedido --</option>
          <option value="soporte">Soporte Técnico</option>
          <option value="soporte_sala">Soporte de Sala</option>
          <option value="insumo">Insumos</option>
          <option value="mobiliario">Mobiliario</option>

          {puedePedirKitRobotica && (
            <option value="kit_robotica">Kit de Robótica</option>
          )}
        </select>

        <div
          className="relative"
          onBlur={() => {
            setTimeout(() => setColegioBuscadorAbierto(false), 120);
          }}
        >
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Colegio
          </label>

          <div className="flex gap-2">
            <input
              type="text"
              value={busquedaColegio}
              onChange={(e) => {
                setBusquedaColegio(e.target.value);
                setColegioSeleccionado('');
                setSalaSeleccionada('');
                setEquipoSeleccionado('');
                setColegioBuscadorAbierto(true);
              }}
              onFocus={() => setColegioBuscadorAbierto(true)}
              className="w-full border px-3 py-2 rounded"
              placeholder="Buscar colegio..."
              autoComplete="off"
              required
            />

            {busquedaColegio && (
              <button
                type="button"
                onClick={limpiarColegio}
                className="px-3 py-2 border rounded text-gray-700 bg-gray-100 hover:bg-gray-200"
                title="Limpiar colegio"
              >
                Limpiar
              </button>
            )}
          </div>

          {colegioBuscadorAbierto && (
            <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg max-h-64 overflow-y-auto">
              {colegiosFiltrados.length > 0 ? (
                colegiosFiltrados.map((colegio) => (
                  <button
                    key={colegio.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => seleccionarColegio(colegio)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 text-gray-800 border-b last:border-b-0"
                  >
                    {colegio.nombre}
                  </button>
                ))
              ) : (
                <div className="px-3 py-3 text-sm text-gray-500">
                  No se encontraron colegios.
                </div>
              )}
            </div>
          )}

          {colegioSeleccionado && (
            <p className="mt-1 text-xs text-green-700">
              Colegio seleccionado
            </p>
          )}
        </div>

        <select
          value={salaSeleccionada}
          onChange={(e) => setSalaSeleccionada(e.target.value)}
          className="w-full border px-3 py-2 rounded"
          required
          disabled={!colegioSeleccionado}
        >
          <option value="">-- Seleccionar sala --</option>
          {salas.map((sala) => (
            <option key={sala.id} value={sala.id}>
              {sala.nombre}
            </option>
          ))}
        </select>

        {tipoPedido === 'soporte' && (
          <select
            value={equipoSeleccionado}
            onChange={(e) => setEquipoSeleccionado(e.target.value)}
            className="w-full border px-3 py-2 rounded"
            required
            disabled={!salaSeleccionada}
          >
            <option value="">-- Seleccionar equipo --</option>
            {equipos.map((eq) => (
              <option key={eq.id} value={eq.id}>
                {eq.nombre}
              </option>
            ))}
          </select>
        )}

        {tipoPedido === 'kit_robotica' && (
          <div className="space-y-3 rounded border border-blue-100 bg-blue-50/50 p-4">
            <div
              className="relative"
              onBlur={() => {
                setTimeout(() => setComponenteBuscadorAbierto(false), 120);
              }}
            >
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Componente o kit solicitado
              </label>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={busquedaComponente}
                  onChange={(e) => {
                    setBusquedaComponente(e.target.value);
                    setComponenteSeleccionado('');
                    setComponenteBuscadorAbierto(true);
                  }}
                  onFocus={() => setComponenteBuscadorAbierto(true)}
                  className="w-full border px-3 py-2 rounded bg-white"
                  placeholder="Buscar componente o kit..."
                  autoComplete="off"
                />

                {busquedaComponente && (
                  <button
                    type="button"
                    onClick={limpiarComponente}
                    className="px-3 py-2 border rounded text-gray-700 bg-white hover:bg-gray-100"
                    title="Limpiar componente"
                  >
                    Limpiar
                  </button>
                )}
              </div>

              {componenteBuscadorAbierto && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg max-h-64 overflow-y-auto">
                  {componentesKitRobotica.length > 0 ? (
                    componentesKitRobotica.map((componente) => (
                      <button
                        key={componente.id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => seleccionarComponente(componente)}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 text-gray-800 border-b last:border-b-0"
                      >
                        <div className="font-semibold">{componente.nombre}</div>
                        {componente.codigo_unico && (
                          <div className="text-xs text-gray-500">{componente.codigo_unico}</div>
                        )}
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-3 text-sm text-gray-500">
                      No se encontraron componentes. Podés mencionarlo en la observación.
                    </div>
                  )}
                </div>
              )}

              {componenteSeleccionado && (
                <p className="mt-1 text-xs text-green-700">
                  Componente seleccionado
                </p>
              )}
            </div>

            <input
              type="number"
              min="1"
              value={cantidad}
              onChange={(e) => setCantidad(e.target.value)}
              className="w-full border px-3 py-2 rounded bg-white"
              placeholder="Cantidad solicitada"
            />
          </div>
        )}

        <textarea
          placeholder={
            tipoPedido === 'kit_robotica'
              ? 'Observación adicional o artículo que no figura en la lista'
              : 'Descripción adicional (opcional)'
          }
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          className="w-full border px-3 py-2 rounded"
          rows={3}
        />

        <button
          type="submit"
          disabled={enviandoPedido}
          className={`bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition ${
            enviandoPedido ? 'opacity-60 cursor-not-allowed' : ''
          }`}
        >
          {enviandoPedido ? 'Enviando...' : 'Enviar pedido'}
        </button>
      </form>
    </div>
  );
};

export default FormularioProfesor;
